import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";

export interface HeroSlide {
  id: string;
  to: string;
  title: string;
  summary: string;
  cover: string;
  badge: string;
}

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      setActive((index + slides.length) % slides.length);
    },
    [slides.length],
  );

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      setActive((v) => (v + 1) % slides.length);
    }, 5500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [slides.length, active]);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      if (diff < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  if (!slides.length) return null;

  return (
    <section className="relative w-full">
      <div
        className="relative w-full h-[420px] md:h-[560px] overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {slides.map((slide, index) => (
          <Link
            key={slide.id}
            to={slide.to}
            className={`absolute inset-0 block transition-opacity duration-700 cursor-pointer ${
              index === active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
            }`}
          >
            <img
              src={slide.cover}
              alt={slide.title}
              title={`${slide.title} - 香蕉动漫`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20"></div>

            <div className="relative z-10 h-full w-full flex items-end pb-14 md:pb-20">
              <div className="w-full px-4 md:px-10">
                <div className="max-w-2xl">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-foreground-950 mb-4">
                    <i className="ri-fire-fill text-sm w-4 h-4 flex items-center justify-center"></i>
                    {slide.badge}
                  </span>
                  <h2 className="font-display font-black text-2xl md:text-4xl lg:text-5xl text-white leading-tight text-balance">
                    {slide.title}
                  </h2>
                  <p className="mt-3 md:mt-4 text-sm md:text-base text-white/85 line-clamp-2 max-w-xl">
                    {slide.summary}
                  </p>
                  <span className="mt-5 md:mt-6 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-white hover:bg-accent-600 transition-colors">
                    查看详情
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}

        <button
          type="button"
          aria-label="上一张"
          onClick={prev}
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/35 text-white hover:bg-black/55 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={next}
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/35 text-white hover:bg-black/55 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张`}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                index === active ? "w-7 bg-primary-500" : "w-2 bg-white/60 hover:bg-white"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}